// src/components/modals/PointsRulesConfigModal.jsx
import React, { useState, useEffect } from 'react';
import { FaCog, FaCalculator, FaSpinner, FaCheck, FaTimes, FaInfoCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';

const PointsRulesConfigModal = ({ isOpen, onClose, onSubmit, marca }) => {
    const [formData, setFormData] = useState({
        pointsEngineVersion: 1, // Siempre automático cuando se configura
        pointsRules: null
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewPoints, setPreviewPoints] = useState(0);

    // Configuración por defecto para el motor automático
    const getDefaultRules = () => ({
        mode: 'RATE_PER_AMOUNT',
        currency: 'EUR',
        rounding: 'ROUND',
        minSpend: 0,
        maxPointsPerTxn: 100,
        ratePerAmount: {
            rate: 1,
            step: 1
        }
    });

    // Cargar datos existentes
    useEffect(() => {
        if (isOpen && marca) {
            if (marca.pointsEngineVersion === 1 && marca.pointsRules) {
                // Si ya tiene configuración, cargarla
                setFormData({
                    pointsEngineVersion: 1,
                    pointsRules: marca.pointsRules
                });
            } else {
                // Si no tiene configuración, inicializar con reglas por defecto
                setFormData({
                    pointsEngineVersion: 1,
                    pointsRules: getDefaultRules()
                });
            }
        }
    }, [isOpen, marca]);

    const handleModeChange = (mode) => {
        // Solo permitir configuración automática
        setFormData({
            pointsEngineVersion: 1,
            pointsRules: getDefaultRules()
        });
    };

    const handleRuleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            pointsRules: {
                ...prev.pointsRules,
                [field]: value
            }
        }));
    };

    const handleNestedRuleChange = (parent, field, value) => {
        setFormData(prev => ({
            ...prev,
            pointsRules: {
                ...prev.pointsRules,
                [parent]: {
                    ...prev.pointsRules[parent],
                    [field]: value
                }
            }
        }));
    };

    const simulatePoints = (amount = 50) => {
        if (!formData.pointsRules) return 0;

        const { mode, ratePerAmount, tiers, multiplierByTag } = formData.pointsRules;

        switch (mode) {
            case 'RATE_PER_AMOUNT':
                const { rate, step } = ratePerAmount;
                return Math.floor(amount / step) * rate;

            case 'TIERED':
                if (!tiers) return 0;
                const tier = tiers.find(t => amount >= t.min && (!t.max || amount <= t.max));
                return tier ? Math.floor(amount * tier.rate) : 0;

            case 'MULTIPLIER_BY_TAG':
                if (!multiplierByTag) return 0;
                return Math.floor(amount * multiplierByTag.baseRate);

            default:
                return 0;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Limpiar datos para enviar solo los campos que el backend acepta
            const cleanData = {
                pointsEngineVersion: formData.pointsEngineVersion,
                pointsRules: {
                    mode: formData.pointsRules.mode,
                    currency: formData.pointsRules.currency,
                    rounding: formData.pointsRules.rounding,
                    minSpend: formData.pointsRules.minSpend,
                    maxPointsPerTxn: formData.pointsRules.maxPointsPerTxn,
                    ratePerAmount: formData.pointsRules.ratePerAmount,
                    tiers: formData.pointsRules.tiers,
                    multiplierByTag: formData.pointsRules.multiplierByTag
                }
            };

            await onSubmit(cleanData);
            Swal.fire({
                title: 'Configuración Guardada',
                text: 'Motor de puntos automático configurado. Los puntos se calcularán automáticamente pero el usuario podrá modificarlos si lo desea.',
                icon: 'success',
                confirmButtonText: 'Aceptar'
            });
            onClose();
        } catch (error) {
            console.error('Error saving points rules:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudo guardar la configuración',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData({
            pointsEngineVersion: 1,
            pointsRules: null
        });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
                {/* Fondo oscuro */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={handleClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Contenedor del modal */}
                <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-4xl mx-auto">
                    <div className="px-6 pt-6 pb-4">
                        {/* Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <FaCalculator className="w-5 h-5 mr-2 text-blue-500" />
                                Configurar Motor de Puntos
                            </h3>
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                                <FaTimes className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Contenido */}
                        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                            {/* Información del motor automático */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <FaInfoCircle className="text-blue-500 mt-1 mr-2 flex-shrink-0" />
                                    <div>
                                        <h4 className="text-sm font-medium text-blue-800 mb-1">Motor de Puntos Automático</h4>
                                        <p className="text-xs text-blue-600 mb-2">
                                            Configura las reglas para calcular puntos automáticamente. El usuario podrá modificar el valor calculado si lo desea.
                                        </p>
                                        <p className="text-xs text-blue-600">
                                            <strong>Beneficio:</strong> Ahorra tiempo y reduce errores, pero mantiene flexibilidad total.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Configuración del motor automático */}
                            {formData.pointsRules && (
                                <div className="space-y-6">
                                    {/* Tipo de cálculo */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            🎯 Método de Cálculo de Puntos
                                        </label>
                                        <select
                                            value={formData.pointsRules.mode}
                                            onChange={(e) => handleRuleChange('mode', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="RATE_PER_AMOUNT">💰 Tasa Fija por Monto (ej: 1 punto por cada 1€)</option>
                                            <option value="TIERED">📊 Sistema por Niveles (ej: más puntos en compras grandes)</option>
                                            <option value="MULTIPLIER_BY_TAG">🏷️ Multiplicadores por Categoría (ej: productos premium x2)</option>
                                        </select>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Selecciona cómo quieres que se calculen los puntos automáticamente
                                        </p>
                                    </div>

                                    {/* Configuración según el tipo */}
                                    {formData.pointsRules.mode === 'RATE_PER_AMOUNT' && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    💎 Cantidad de Puntos por Unidad
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    value={formData.pointsRules.ratePerAmount?.rate || 1}
                                                    onChange={(e) => handleNestedRuleChange('ratePerAmount', 'rate', Number(e.target.value))}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="1"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Cuántos puntos otorgar por cada unidad monetaria
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    💰 Cada Cuánto Dinero se Otorga 1 Punto
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0.01"
                                                    step="0.01"
                                                    value={formData.pointsRules.ratePerAmount?.step || 1}
                                                    onChange={(e) => handleNestedRuleChange('ratePerAmount', 'step', Number(e.target.value))}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="1"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Ejemplo: Si pones 5, significa que por cada 5€ gastados se otorga 1 punto. Si el cliente gasta 7€, solo obtiene 1 punto (no 1.4)
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Configuración de niveles */}
                                    {formData.pointsRules.mode === 'TIERED' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Niveles de Puntos
                                            </label>
                                            <div className="space-y-3">
                                                {formData.pointsRules.tiers?.map((tier, index) => (
                                                    <div key={index} className="flex items-center space-x-3">
                                                        <input
                                                            type="number"
                                                            placeholder="Mínimo"
                                                            value={tier.min || 0}
                                                            onChange={(e) => {
                                                                const newTiers = [...formData.pointsRules.tiers];
                                                                newTiers[index] = { ...tier, min: Number(e.target.value) };
                                                                handleRuleChange('tiers', newTiers);
                                                            }}
                                                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                        <span className="text-gray-500">-</span>
                                                        <input
                                                            type="number"
                                                            placeholder="Máximo"
                                                            value={tier.max || ''}
                                                            onChange={(e) => {
                                                                const newTiers = [...formData.pointsRules.tiers];
                                                                newTiers[index] = { ...tier, max: e.target.value ? Number(e.target.value) : undefined };
                                                                handleRuleChange('tiers', newTiers);
                                                            }}
                                                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                        <span className="text-gray-500">€</span>
                                                        <span className="text-gray-500">→</span>
                                                        <input
                                                            type="number"
                                                            placeholder="Tasa"
                                                            step="0.1"
                                                            value={tier.rate || 0}
                                                            onChange={(e) => {
                                                                const newTiers = [...formData.pointsRules.tiers];
                                                                newTiers[index] = { ...tier, rate: Number(e.target.value) };
                                                                handleRuleChange('tiers', newTiers);
                                                            }}
                                                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                        <span className="text-gray-500">puntos/€</span>
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newTiers = [...(formData.pointsRules.tiers || []), { min: 0, rate: 1 }];
                                                        handleRuleChange('tiers', newTiers);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                                >
                                                    + Añadir Nivel
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Configuración de multiplicadores */}
                                    {formData.pointsRules.mode === 'MULTIPLIER_BY_TAG' && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Tasa Base
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    value={formData.pointsRules.multiplierByTag?.baseRate || 1}
                                                    onChange={(e) => handleNestedRuleChange('multiplierByTag', 'baseRate', Number(e.target.value))}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Multiplicadores por Categoría
                                                </label>
                                                <div className="space-y-2">
                                                    {formData.pointsRules.multiplierByTag?.multipliers?.map((mult, index) => (
                                                        <div key={index} className="flex items-center space-x-3">
                                                            <input
                                                                type="text"
                                                                placeholder="Categoría (ej: premium)"
                                                                value={mult.tag || ''}
                                                                onChange={(e) => {
                                                                    const newMultipliers = [...formData.pointsRules.multiplierByTag.multipliers];
                                                                    newMultipliers[index] = { ...mult, tag: e.target.value };
                                                                    handleNestedRuleChange('multiplierByTag', 'multipliers', newMultipliers);
                                                                }}
                                                                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                            />
                                                            <span className="text-gray-500">×</span>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.1"
                                                                placeholder="Multiplicador"
                                                                value={mult.rate || 1}
                                                                onChange={(e) => {
                                                                    const newMultipliers = [...formData.pointsRules.multiplierByTag.multipliers];
                                                                    newMultipliers[index] = { ...mult, rate: Number(e.target.value) };
                                                                    handleNestedRuleChange('multiplierByTag', 'multipliers', newMultipliers);
                                                                }}
                                                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                            />
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newMultipliers = [...(formData.pointsRules.multiplierByTag?.multipliers || []), { tag: '', rate: 1 }];
                                                            handleNestedRuleChange('multiplierByTag', 'multipliers', newMultipliers);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                                    >
                                                        + Añadir Multiplicador
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Configuraciones adicionales */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                💱 Moneda de Referencia
                                            </label>
                                            <select
                                                value={formData.pointsRules.currency || 'EUR'}
                                                onChange={(e) => handleRuleChange('currency', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="EUR">🇪🇺 Euro (€)</option>
                                                <option value="USD">🇺🇸 Dólar Americano ($)</option>
                                                <option value="PEN">🇵🇪 Sol Peruano (S/)</option>
                                                <option value="CLP">🇨🇱 Peso Chileno ($)</option>
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Moneda en la que se basarán los cálculos de puntos
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                🔢 Método de Redondeo
                                            </label>
                                            <select
                                                value={formData.pointsRules.rounding || 'ROUND'}
                                                onChange={(e) => handleRuleChange('rounding', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="NONE">Sin redondeo (ej: 1.7 puntos = 1.7)</option>
                                                <option value="FLOOR">Redondear hacia abajo (ej: 1.7 puntos = 1)</option>
                                                <option value="CEIL">Redondear hacia arriba (ej: 1.7 puntos = 2)</option>
                                                <option value="ROUND">Redondear normal (ej: 1.7 puntos = 2)</option>
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Cómo manejar los decimales en el cálculo de puntos
                                            </p>
                                        </div>
                                    </div>

                                    {/* Límites */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                💰 Gasto Mínimo Requerido
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={formData.pointsRules.minSpend || 0}
                                                onChange={(e) => handleRuleChange('minSpend', Number(e.target.value))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="0"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                Monto mínimo que debe gastar el cliente para obtener puntos (0 = sin mínimo)
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                🚫 Límite Máximo de Puntos por Compra
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={formData.pointsRules.maxPointsPerTxn || 100}
                                                onChange={(e) => handleRuleChange('maxPointsPerTxn', Number(e.target.value))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="100"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                Máximo de puntos que puede obtener en una sola transacción (0 = sin límite)
                                            </p>
                                        </div>
                                    </div>

                                    {/* Preview de cálculo */}
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                        <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center">
                                            <FaInfoCircle className="mr-1" />
                                            📊 Vista Previa de Cálculos
                                        </h4>
                                        <p className="text-xs text-green-600 mb-3">
                                            Ejemplos de cuántos puntos obtendrían tus clientes con diferentes montos de compra:
                                        </p>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                            <div className="bg-white border border-green-200 rounded-lg p-3">
                                                <div className="text-center">
                                                    <span className="text-green-600 font-medium">Compra de 25€</span>
                                                    <div className="text-lg font-bold text-green-800 mt-1">
                                                        {simulatePoints(25)} puntos
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="bg-white border border-green-200 rounded-lg p-3">
                                                <div className="text-center">
                                                    <span className="text-green-600 font-medium">Compra de 50€</span>
                                                    <div className="text-lg font-bold text-green-800 mt-1">
                                                        {simulatePoints(50)} puntos
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="bg-white border border-green-200 rounded-lg p-3">
                                                <div className="text-center">
                                                    <span className="text-green-600 font-medium">Compra de 100€</span>
                                                    <div className="text-lg font-bold text-green-800 mt-1">
                                                        {simulatePoints(100)} puntos
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Botones */}
                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <FaCheck className="w-4 h-4 mr-2" />
                                            Guardar Configuración
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PointsRulesConfigModal;
